import { api } from "@/lib/api";
export interface Shift {
  id: string;
  name: string;
  startMinute: number;
  endMinute: number;
}
export interface Event {
  id: string;
  action: string;
  occurredAt: string;
  result: string;
  reason?: string;
  rejectReason?: string;
  supersededById?: string;
}
export interface Evaluation {
  id?: string;
  profileId?: string;
  shiftDate: string;
  shiftTemplateId: string | null;
  status: string;
  scheduledMinutes: number;
  workedMinutes: number;
  lateMinutes: number;
  earlyLeaveMinutes: number;
  excusedMinutes: number;
  unpaidExcusedMinutes: number;
  deductibleMinutes: number;
  settingsVersion: number;
  explanation: string[];
  events?: Event[];
  lockedByPayslipId?: string;
}
export interface Today {
  serverNow: string;
  profileId: string;
  attendanceRequired: boolean;
  locationRequired: boolean;
  shifts: Shift[];
  /** Only a session still inside its shift + grace; never a forgotten one. */
  openSession: { id: string } | null;
  /** An accepted check-in exists for today (clinic wall clock). */
  checkedInToday?: boolean;
  unresolvedSessions?: UnresolvedSession[];
  lastEvent: Event | null;
  nextShifts: { date: string; shifts: Shift[] }[];
}
export interface Request {
  id: string;
  profileId: string;
  kind: "leave" | "departure" | "correction";
  requestKind?: string;
  status: string;
  reason: string;
  /** Approver's reason on leave/departure (or SELF_CANCELLED). */
  note?: string | null;
  /** Approver's reason on a correction. */
  decisionNote?: string | null;
  decidedAt?: string | null;
  attachmentFileId?: string;
  startDate?: string;
  endDate?: string;
  date?: string;
  proposedAt?: string;
  leaveType?: { nameAr: string; nameEn?: string };
  profile?: { userId?: string; user: { fullNameAr: string; fullNameEn?: string } };
  /** Who filed a correction (null for SYSTEM_GENERATED). */
  requestedById?: string | null;
  /** USER, or SYSTEM_GENERATED for an automatic missing check-out. */
  source?: string;
  resolution?: "CHECKED_OUT" | "SESSION_VOIDED" | null;
  targetSession?: {
    id: string;
    status: string;
    shiftDate: string;
    unresolvedAt?: string | null;
    checkInEvent: { occurredAt: string };
    checkOutEvent?: { occurredAt: string; source: string } | null;
    shiftTemplate?: { name: string; startMinute: number; endMinute: number } | null;
  } | null;
}
/** A previous session that passed its shift end + grace without a check-out. */
export interface UnresolvedSession {
  id: string;
  shiftDate: string;
  unresolvedAt?: string | null;
  checkInEvent: { occurredAt: string };
  shiftTemplate?: { name: string; startMinute: number; endMinute: number } | null;
  request: { id: string; status: string; decidedAt?: string | null } | null;
}
export interface Daily {
  profileId: string;
  employee: { fullNameAr: string; fullNameEn?: string };
  evaluations: Evaluation[];
  events: Event[];
}
export const getAttendance = async <T>(
  path: string,
  params?: Record<string, unknown>,
): Promise<T> => (await api.get<T>("/hr/" + path, { params })).data;
export const postAttendance = async <T = unknown>(
  path: string,
  body: unknown,
): Promise<T> => (await api.post<T>("/hr/" + path, body)).data;
